import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import Confetti from 'react-confetti';
import {
    Leaf,
    Star,
    ArrowLeft,
    Check,
    CheckCircle,
    Camera,
    Lightning
} from '@phosphor-icons/react';

export default function MissionDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, api, updateUser, isChild } = useAuth();
    const [mission, setMission] = useState(null);
    const [loading, setLoading] = useState(true);
    const [completing, setCompleting] = useState(false);
    const [checkedSteps, setCheckedSteps] = useState([]);
    const [showConfetti, setShowConfetti] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);

    useEffect(() => {
        fetchMission();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchMission = async () => {
        try {
            const response = await api().get(`/missions/${id}`);
            setMission(response.data);
            
            // Check if already completed
            if (user?.completed_missions?.includes(id)) {
                setIsCompleted(true);
                setCheckedSteps(response.data.instructions.map((_, i) => i));
            }
        } catch (error) {
            toast.error('Failed to load mission');
            navigate('/missions');
        } finally {
            setLoading(false);
        }
    };

    const toggleStep = (index) => {
        if (isCompleted) return;
        setCheckedSteps(prev => 
            prev.includes(index) 
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    const handleCompleteMission = async () => {
        if (!isChild) {
            toast.error('Only rangers can complete missions!');
            return;
        }
        
        if (checkedSteps.length < mission.instructions.length) {
            toast.error('Please complete all steps first!');
            return;
        }

        setCompleting(true);
        try {
            const response = await api().post('/missions/complete', {
                mission_id: id
            });
            
            // Update user state
            updateUser({
                xp: response.data.xp,
                level: response.data.level,
                badges: response.data.badges,
                completed_missions: [...(user?.completed_missions || []), id]
            });
            
            setShowConfetti(true);
            setIsCompleted(true);
            toast.success(`Mission Complete! +${mission.xp_reward} Bush Tokens`);
            
            setTimeout(() => setShowConfetti(false), 5000);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to complete mission');
        } finally {
            setCompleting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] flex items-center justify-center">
                <div className="brutalist-card p-8 text-center">
                    <div className="w-16 h-16 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="font-fredoka text-lg">Loading mission...</p>
                </div>
            </div>
        );
    }

    if (!mission) return null;

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
            {showConfetti && <Confetti recycle={false} numberOfPieces={300} />}
            
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                            <div className="w-10 h-10 rounded-full bg-[#22c55e] border-4 border-[#1e293b] flex items-center justify-center">
                                <Leaf weight="fill" className="text-white" size={20} />
                            </div>
                            <span className="font-fredoka text-xl font-bold text-[#1e293b] hidden sm:block">
                                Bush Rangers
                            </span>
                        </Link>
                        
                        <Link 
                            to="/missions"
                            className="flex items-center gap-2 text-[#475569] hover:text-[#22c55e] font-semibold"
                            data-testid="back-to-missions"
                        >
                            <ArrowLeft weight="bold" size={20} />
                            <span className="hidden sm:inline">Back to Missions</span>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="pt-20 px-4 pb-8 max-w-4xl mx-auto">
                {/* Hero */}
                <motion.div 
                    className="brutalist-card overflow-hidden mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <div className={`h-48 md:h-64 relative ${
                        mission.ecosystem === 'bush' ? 'ecosystem-bush' :
                        mission.ecosystem === 'reef' ? 'ecosystem-reef' :
                        'ecosystem-outback'
                    }`}>
                        {mission.image_url && (
                            <img 
                                src={mission.image_url} 
                                alt={mission.title}
                                className="w-full h-full object-cover opacity-60"
                            />
                        )}
                        <div className="absolute inset-0 flex items-end p-6 bg-gradient-to-t from-black/60 to-transparent">
                            <div>
                                <div className="flex gap-2 mb-3">
                                    <span className={`px-3 py-1 rounded-full text-sm font-bold border-2 border-white ${
                                        mission.difficulty === 'easy' ? 'bg-[#22c55e] text-white' :
                                        mission.difficulty === 'medium' ? 'bg-[#fde047] text-[#1e293b]' :
                                        'bg-[#fb7185] text-white'
                                    }`}>
                                        {mission.difficulty}
                                    </span>
                                    <span className="px-3 py-1 rounded-full text-sm font-bold bg-white/20 text-white capitalize">
                                        {mission.category}
                                    </span>
                                </div>
                                <h1 className="font-fredoka text-2xl md:text-4xl font-bold text-white">
                                    {mission.title}
                                </h1>
                            </div>
                        </div>
                        {isCompleted && (
                            <div className="absolute top-4 right-4">
                                <div className="bg-[#22c55e] text-white px-4 py-2 rounded-full font-fredoka font-bold flex items-center gap-2 border-4 border-white">
                                    <CheckCircle weight="fill" size={24} />
                                    Completed!
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>

                <div className="grid md:grid-cols-3 gap-6">
                    {/* Mission Details */}
                    <motion.div 
                        className="md:col-span-2 space-y-6"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        {/* Description */}
                        <div className="brutalist-card p-6">
                            <h2 className="font-fredoka text-xl font-bold text-[#1e293b] mb-3">
                                About This Mission
                            </h2>
                            <p className="text-[#475569] leading-relaxed">
                                {mission.description}
                            </p>
                        </div>

                        {/* Instructions */}
                        <div className="brutalist-card p-6">
                            <h2 className="font-fredoka text-xl font-bold text-[#1e293b] mb-4">
                                Steps to Complete
                            </h2>
                            <div className="space-y-3">
                                {mission.instructions.map((instruction, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.1 * index }}
                                        className={`flex items-start gap-4 p-4 rounded-xl border-4 cursor-pointer transition-all ${
                                            checkedSteps.includes(index)
                                                ? 'border-[#22c55e] bg-[#22c55e]/10'
                                                : 'border-[#1e293b]/20 hover:border-[#22c55e]/50'
                                        }`}
                                        onClick={() => toggleStep(index)}
                                        data-testid={`step-${index}`}
                                    >
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-4 ${
                                            checkedSteps.includes(index)
                                                ? 'bg-[#22c55e] border-[#1e293b]'
                                                : 'bg-white border-[#1e293b]'
                                        }`}>
                                            {checkedSteps.includes(index) ? (
                                                <Check weight="bold" className="text-white" size={18} />
                                            ) : (
                                                <span className="font-fredoka font-bold text-[#1e293b]">{index + 1}</span>
                                            )}
                                        </div>
                                        <p className={`font-semibold ${checkedSteps.includes(index) ? 'line-through text-[#475569]' : 'text-[#1e293b]'}`}>
                                            {instruction}
                                        </p>
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Photo Proof (if required) */}
                        {mission.proof_type === 'photo' && !isCompleted && (
                            <div className="brutalist-card p-6 bg-[#fde047]/20">
                                <div className="flex items-center gap-3 mb-3">
                                    <Camera weight="duotone" className="text-[#1e293b]" size={24} />
                                    <h3 className="font-fredoka font-bold text-[#1e293b]">
                                        Photo Proof Required
                                    </h3>
                                </div>
                                <p className="text-[#475569] text-sm">
                                    This mission requires photo proof. Photo upload feature coming soon - for now, complete by checking all steps!
                                </p>
                            </div>
                        )}
                    </motion.div>

                    {/* Sidebar */}
                    <motion.div 
                        className="space-y-6"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        {/* Rewards */}
                        <div className="brutalist-card p-6 bg-gradient-to-br from-[#fde047] to-[#fbbf24]">
                            <h3 className="font-fredoka text-lg font-bold text-[#1e293b] mb-4">
                                Rewards
                            </h3>
                            <div className="space-y-4">
                                <div className="flex items-center gap-3 bg-white/50 rounded-xl p-3">
                                    <Star weight="fill" className="text-[#1e293b]" size={32} />
                                    <div>
                                        <p className="font-fredoka text-2xl font-bold text-[#1e293b]">
                                            +{mission.xp_reward}
                                        </p>
                                        <p className="text-sm text-[#1e293b]/70 font-semibold">Bush Tokens</p>
                                    </div>
                                </div>
                                {mission.badge_reward && (
                                    <div className="flex items-center gap-3 bg-white/50 rounded-xl p-3">
                                        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-xl">
                                            🏅
                                        </div>
                                        <div>
                                            <p className="font-fredoka font-bold text-[#1e293b]">
                                                Badge Unlock
                                            </p>
                                            <p className="text-sm text-[#1e293b]/70">Special reward!</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Complete Button */}
                        {isChild && !isCompleted && (
                            <motion.button
                                onClick={handleCompleteMission}
                                disabled={completing || checkedSteps.length < mission.instructions.length}
                                whileHover={{ y: -2, x: -2 }}
                                whileTap={{ y: 2, x: 2 }}
                                className={`brutalist-btn w-full text-lg flex items-center justify-center gap-2 ${
                                    checkedSteps.length >= mission.instructions.length
                                        ? 'bg-[#22c55e] text-white pulse-glow'
                                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                }`}
                                data-testid="complete-mission-btn"
                            >
                                {completing ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Completing...
                                    </>
                                ) : (
                                    <>
                                        <Lightning weight="fill" size={24} />
                                        Complete Mission
                                    </>
                                )}
                            </motion.button>
                        )}

                        {isCompleted && (
                            <div className="brutalist-card p-6 bg-[#22c55e] text-white text-center">
                                <CheckCircle weight="fill" className="mx-auto mb-2" size={48} />
                                <p className="font-fredoka text-xl font-bold">Mission Complete!</p>
                                <p className="text-white/80">You earned {mission.xp_reward} Bush Tokens</p>
                            </div>
                        )}

                        {!isChild && (
                            <div className="brutalist-card p-6 bg-[#38bdf8]/20 text-center">
                                <p className="text-[#1e293b] font-semibold">
                                    Switch to a child account to complete missions!
                                </p>
                            </div>
                        )}

                        {/* Progress */}
                        <div className="brutalist-card p-6">
                            <h3 className="font-fredoka font-bold text-[#1e293b] mb-3">
                                Your Progress
                            </h3>
                            <div className="progress-bar">
                                <div 
                                    className="progress-bar-fill"
                                    style={{ width: `${(checkedSteps.length / mission.instructions.length) * 100}%` }}
                                ></div>
                            </div>
                            <p className="text-center text-sm text-[#475569] mt-2 font-semibold">
                                {checkedSteps.length} of {mission.instructions.length} steps
                            </p>
                        </div>
                    </motion.div>
                </div>
            </main>
        </div>
    );
}
